/** 我的作業:日期分組、三狀態卡、檔案上傳、預設錨點。
 *  自 public/js/homework.js 移植。
 */
import { request } from './api';
import type { Assignment } from './types';

const WD = ['日', '一', '二', '三', '四', '五', '六'];

const state = {
  filter: 'all' as 'all' | 'pending' | 'graded',
  items: [] as Assignment[],
};

const p2 = (n: number) => String(n).padStart(2, '0');

function fmtDate(v: string): string {
  const d = new Date(v);
  return `${p2(d.getMonth() + 1)} / ${p2(d.getDate())}(${WD[d.getDay()]})`;
}

function fmtDT(v: string): string {
  const d = new Date(v);
  return `${p2(d.getMonth() + 1)}/${p2(d.getDate())} ${p2(d.getHours())}:${p2(d.getMinutes())}`;
}

/** 作業標題與評語為使用者/教師輸入,渲染前擋掉尖括號 */
const esc = (s: unknown) => String(s ?? '').replace(/</g, '&lt;');

function statusBadge(x: Assignment): string {
  if (x.status === 'graded') return '<span class="badge badge--green">已批改</span>';
  if (x.status === 'submitted') return '<span class="badge badge--purple">審查中</span>';
  const days = Math.ceil((new Date(x.due_at).getTime() - Date.now()) / 86400000);
  if (days < 0) return '<span class="badge badge--grey">已逾期</span>';
  return `<span class="badge badge--red">倒數 ${days} 天</span>`;
}

function cardHtml(x: Assignment): string {
  let h = `<div class="hw__card" data-id="${x.id}" data-status="${x.status}">`;
  h += `<div class="hw__card-top"><div class="hw__card-tags">${statusBadge(x)}`
    + `<span class="hw__course-name">${esc(x.course_name)}</span></div>`;

  if (x.status === 'graded') {
    h += `<div class="hw__score">${x.score}<span>/ 100</span></div>`;
  } else if (x.status === 'submitted') {
    const late = new Date(x.submitted_at ?? 0) > new Date(x.due_at) ? '遲交' : '準時繳交';
    h += `<span class="sx248">已於 ${fmtDT(x.submitted_at ?? '')} ${late}</span>`;
  } else {
    const d = new Date(x.due_at);
    h += `<span class="sx250">截止時間 ${p2(d.getHours())}:${p2(d.getMinutes())}</span>`;
  }
  h += '</div>';
  h += `<h3 class="hw__title">${esc(x.title)}</h3>`;

  if (x.status === 'graded') {
    const attach = x.feedback_file
      ? '<div class="hw__feedback-attach">老師附帶文件:'
        + `<a href="/api/student/submissions/${x.submission_id}/file?type=feedback">`
        + `${esc(x.feedback_file.split('/').pop())}</a></div>`
      : '';
    h += '<div class="hw__feedback">'
      + '<div class="hw__feedback-header"><strong>老師評語('
      + `${esc(x.grader_name ?? '')}${esc(x.grader_title ?? '')})</strong>`
      + `<time>批改時間:${fmtDT(x.graded_at ?? '')}</time></div>`
      + `<p class="hw__feedback-body">${esc(x.teacher_comment)}</p>`
      + attach
      + '</div>';
  } else if (x.status === 'submitted') {
    h += '<div class="hw__feedback-attach">附帶文件:'
      + `<a class="ic-6c61e1" href="/api/student/submissions/${x.submission_id}/file">`
      + `${esc(x.sub_filename)}</a></div>`;
  } else {
    if (x.description) {
      h += `<div class="hw__feedback"><p class="hw__feedback-body">${esc(x.description)}`
        + (x.late_penalty_note ? `(${esc(x.late_penalty_note)})` : '')
        + '</p></div>';
    }
    h += '<div class="hw__submit-row">'
      + `<div class="hw__file-pick"><input type="file" id="hw-file-${x.id}" style="display:none">`
      + `<button class="hw__file-pick-btn" data-pick="${x.id}">選擇檔案</button>`
      + `<span class="hw__file-pick-hint" id="hw-hint-${x.id}">尚未選擇任何檔案...</span></div>`
      + `<button class="btn btn--primary" data-upload="${x.id}">確認上傳作業</button>`
      + '</div>';
  }
  return `${h}</div>`;
}

function render(): void {
  const host = document.getElementById('hw-list');
  if (!host) return;

  const items = state.items.filter((x) => {
    if (state.filter === 'pending') return x.status === 'pending';
    if (state.filter === 'graded') return x.status === 'graded';
    return true;
  });
  if (!items.length) {
    host.innerHTML = '<div style="color:#8C8C8C;padding:24px 0">沒有符合條件的作業</div>';
    return;
  }

  // 依截止日期分組(由舊至新)
  const groups: Record<string, Assignment[]> = {};
  const order: string[] = [];
  for (const x of items) {
    const key = x.due_at.slice(0, 10);
    if (!groups[key]) {
      groups[key] = [];
      order.push(key);
    }
    groups[key]?.push(x);
  }

  host.innerHTML = order.map((key) => {
    const list = groups[key] ?? [];
    const first = list[0];
    if (!first) return '';
    const pending = list.filter((x) => x.status === 'pending').length;
    const graded = list.filter((x) => x.status === 'graded').length;
    let badge: string;
    if (pending) badge = `<span class="badge badge--red">${pending}項作業待繳交</span>`;
    else if (graded === list.length) {
      badge = `<span class="badge badge--green">${graded}項作業已批改完成</span>`;
    } else badge = `<span class="badge badge--purple">${list.length}項作業審查中</span>`;

    return '<div class="hw__group">'
      + '<div class="hw__group-header"><div class="hw__group-header-left">'
      + `<span class="hw__group-header-date">${fmtDate(first.due_at)}</span>${badge}</div></div>`
      + list.map(cardHtml).join('')
      + '</div>';
  }).join('');

  bindCards();
}

function bindCards(): void {
  document.querySelectorAll<HTMLElement>('[data-pick]').forEach((btn) => {
    btn.addEventListener('click', () => {
      document.getElementById(`hw-file-${btn.dataset.pick}`)?.click();
    });
  });

  document.querySelectorAll<HTMLInputElement>('input[type=file][id^=hw-file-]').forEach((input) => {
    input.addEventListener('change', () => {
      const id = input.id.replace('hw-file-', '');
      const hint = document.getElementById(`hw-hint-${id}`);
      if (hint) {
        hint.textContent = input.files?.length
          ? (input.files[0]?.name ?? '') : '尚未選擇任何檔案...';
      }
    });
  });

  document.querySelectorAll<HTMLButtonElement>('[data-upload]').forEach((btn) => {
    btn.addEventListener('click', () => {
      void (async () => {
        const id = btn.dataset.upload;
        const input = document.getElementById(`hw-file-${id}`) as HTMLInputElement | null;
        const file = input?.files?.[0];
        if (!file) {
          window.alert('請先選擇檔案');
          return;
        }
        const fd = new FormData();
        fd.append('file', file);
        btn.disabled = true;
        btn.textContent = '上傳中…';
        try {
          await request(`/api/assignments/${id}/submit`, { method: 'POST', body: fd });
          await load();
        } catch (e) {
          window.alert(`上傳失敗:${e instanceof Error ? e.message : String(e)}`);
          btn.disabled = false;
          btn.textContent = '確認上傳作業';
        }
      })();
    });
  });
}

function updateFilterCounts(): void {
  const counts = {
    all: state.items.length,
    pending: state.items.filter((x) => x.status === 'pending').length,
    graded: state.items.filter((x) => x.status === 'graded').length,
  };
  document.querySelectorAll<HTMLElement>('#hw-filters button').forEach((b) => {
    const f = b.dataset.filter as keyof typeof counts | undefined;
    if (!f || !(f in counts)) return;
    let label: string;
    if (f === 'all') label = '全部';
    else if (f === 'pending') label = '待繳交';
    else label = '已批改';
    b.textContent = `${label}(${counts[f]})`;
    b.className = `btn ${state.filter === f ? 'btn--primary' : 'btn--outline'}`;
  });
}

/** 錨點:未繳交且期限將至的第一筆;若都已繳交則錨至最後 */
function anchor(): void {
  let target = document.querySelector<HTMLElement>('.hw__card[data-status="pending"]');
  const cards = document.querySelectorAll<HTMLElement>('.hw__card');
  if (!target && cards.length) target = cards[cards.length - 1] ?? null;
  target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

async function load(skipAnchor = false): Promise<void> {
  state.items = await request<Assignment[]>('/api/student/assignments');
  updateFilterCounts();
  render();
  if (!skipAnchor) window.setTimeout(anchor, 150);
}

/** 初始化我的作業頁;頁面沒有 #hw-list 時不做事 */
export async function initHomework(): Promise<void> {
  if (!document.getElementById('hw-list')) return;
  document.querySelectorAll<HTMLElement>('#hw-filters button').forEach((b) => {
    b.addEventListener('click', () => {
      const f = b.dataset.filter;
      if (f === 'all' || f === 'pending' || f === 'graded') state.filter = f;
      updateFilterCounts();
      render();
    });
  });
  await load();
}
